import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { MentionItem } from '@/components/MarkdownDisplay'
import { MENTION_PATTERN_WITH_CAPTURE_REGEX } from '@/features/view-tfl-uploaded/constants'
import { cn } from '@/utils/common'

export interface MarkdownDisplayProps {
  content: string
  className?: string
}

// Process text to render mentions inline
const processTextWithMentions = (
  text: string
): Array<string | React.ReactElement> => {
  const mentionRegex = new RegExp(MENTION_PATTERN_WITH_CAPTURE_REGEX)
  const parts: Array<string | React.ReactElement> = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyCounter = 0

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }

    // Add the mention component
    const userName = match[1]
    const userId = match[2]
    parts.push(
      <MentionItem
        key={`mention-${keyCounter++}-${match.index}`}
        userName={userName}
        userId={userId}
      />
    )

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

// Helper to find all placeholder matches in a string
const findPlaceholderMatches = (
  text: string,
  mentions: Map<string, { userName: string; userId: string }>
): Array<{
  placeholder: string
  index: number
  userName: string
  userId: string
}> => {
  const placeholderMatches: Array<{
    placeholder: string
    index: number
    userName: string
    userId: string
  }> = []

  for (const [placeholder, { userName, userId }] of mentions.entries()) {
    let searchIndex = 0
    let index = text.indexOf(placeholder, searchIndex)
    while (index !== -1) {
      placeholderMatches.push({ placeholder, index, userName, userId })
      searchIndex = index + placeholder.length
      index = text.indexOf(placeholder, searchIndex)
    }
  }

  placeholderMatches.sort((a, b) => a.index - b.index)
  return placeholderMatches
}

// Helper to build result from placeholder matches
const buildResultFromPlaceholders = (
  text: string,
  placeholderMatches: Array<{
    placeholder: string
    index: number
    userName: string
    userId: string
  }>,
  mentions: Map<string, { userName: string; userId: string }>,
  processChildrenWithPlaceholdersFn: (
    children: React.ReactNode,
    mentions?: Map<string, { userName: string; userId: string }>
  ) => React.ReactNode
): React.ReactNode[] => {
  const result: React.ReactNode[] = []
  let lastIndex = 0

  placeholderMatches.forEach((match, matchIndex) => {
    // Add text before this placeholder
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index)
      if (textBefore) {
        const processed = processChildrenWithPlaceholdersFn(
          textBefore,
          mentions
        )
        if (Array.isArray(processed)) {
          result.push(...processed)
        } else {
          result.push(processed)
        }
      }
    }

    // Add the MentionItem
    result.push(
      <MentionItem
        key={`mention-${match.placeholder}-${matchIndex}`}
        userName={match.userName}
        userId={match.userId}
      />
    )

    lastIndex = match.index + match.placeholder.length
  })

  // Add remaining text after last placeholder
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex)
    if (textAfter) {
      const processed = processChildrenWithPlaceholdersFn(textAfter, mentions)
      if (Array.isArray(processed)) {
        result.push(...processed)
      } else {
        result.push(processed)
      }
    }
  }

  return result
}

// Helper to process React children and replace placeholders with MentionBadge components
const processChildrenWithPlaceholders = (
  children: React.ReactNode,
  mentions?: Map<string, { userName: string; userId: string }>
): React.ReactNode => {
  if (typeof children === 'string') {
    if (mentions && mentions.size > 0) {
      const placeholderMatches = findPlaceholderMatches(children, mentions)
      if (placeholderMatches.length > 0) {
        const result = buildResultFromPlaceholders(
          children,
          placeholderMatches,
          mentions,
          processChildrenWithPlaceholders
        )
        return <>{result}</>
      }
    }
    return <>{processTextWithMentions(children)}</>
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      // Generate a more stable key by combining child type and index
      const childKey =
        typeof child === 'object' &&
        child !== null &&
        'key' in child &&
        child.key !== null
          ? `child-${String(child.key)}-${index}`
          : `child-${typeof child}-${index}`
      return (
        <React.Fragment key={childKey}>
          {processChildrenWithPlaceholders(child, mentions)}
        </React.Fragment>
      )
    })
  }

  return children
}

// Preprocess content: replace mentions with a special marker that we'll process
// This ensures mentions are caught before ReactMarkdown processes them
const preprocessContent = (
  content: string
): {
  processedContent: string
  mentions: Map<string, { userName: string; userId: string }>
} => {
  const mentions = new Map<string, { userName: string; userId: string }>()
  const mentionRegex = new RegExp(MENTION_PATTERN_WITH_CAPTURE_REGEX)
  let counter = 0

  const processedContent = content.replace(
    mentionRegex,
    (_match, userName, userId) => {
      // Use a placeholder format that won't be parsed as markdown
      // Format: MENTION_PLACEHOLDER_counter to avoid markdown parsing
      // Using uppercase and underscores to avoid markdown interpretation
      const placeholder = `MENTION_PLACEHOLDER_${counter}`
      mentions.set(placeholder, { userName, userId })
      counter++
      return placeholder
    }
  )

  return { processedContent, mentions }
}

// Component factories that accept processChildren function
const createParagraph = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => {
    const processedChildren = processChildren(children)
    return <p className="text-sm text-gray-900 my-1">{processedChildren}</p>
  }
}

const createHeading1 = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-3xl font-bold mb-2">{processChildren(children)}</h1>
  )
}

const createHeading2 = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-2xl font-bold mb-2">{processChildren(children)}</h2>
  )
}

const createListItem = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <li className="mb-1">{processChildren(children)}</li>
  )
}

const createStrong = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-bold">{processChildren(children)}</strong>
  )
}

const createEm = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <em className="italic">{processChildren(children)}</em>
  )
}

const createBlockquote = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border border-l-2 border-solid border-white border-l-gray-light-200 ml-0 mr-0 pl-2 text-gray-light-400 italic">
      {processChildren(children)}
    </blockquote>
  )
}

const createText = (
  processChildren: (children: React.ReactNode) => React.ReactNode
) => {
  return (props: { children?: React.ReactNode }) =>
    processChildren(props.children)
}

const UnorderedList = ({ children }: { children?: React.ReactNode }) => (
  <ul className="mb-2 ml-0 list-disc">{children}</ul>
)

const OrderedList = ({ children }: { children?: React.ReactNode }) => (
  <ol className="mb-2 ml-0 list-decimal">{children}</ol>
)

const PreBlock = ({ children }: { children?: React.ReactNode }) => (
  <pre className="mb-2">{children}</pre>
)

const CodeBlock = (props: {
  children?: React.ReactNode
  inline?: boolean
  node?: unknown
  [key: string]: unknown
}) => {
  const { children, inline, node: _node } = props
  const isInline = inline !== false
  return isInline ? (
    <code className="rounded bg-gray-200 px-1 py-1 font-mono text-sm text-gray-800">
      {children}
    </code>
  ) : (
    <code className="block rounded bg-gray-200 px-2 py-2 font-mono text-sm text-gray-800 mb-2">
      {children}
    </code>
  )
}

const MarkdownDisplay: React.FC<MarkdownDisplayProps> = ({
  content,
  className,
}) => {
  // Preprocess content to replace mentions with placeholders
  const { processedContent, mentions } = preprocessContent(content)

  // Create a function to process children with placeholders
  const processChildren = (children: React.ReactNode) =>
    processChildrenWithPlaceholders(children, mentions)

  // Create component renderers with processChildren
  const Paragraph = createParagraph(processChildren)
  const Heading1 = createHeading1(processChildren)
  const Heading2 = createHeading2(processChildren)
  const ListItem = createListItem(processChildren)
  const Strong = createStrong(processChildren)
  const Em = createEm(processChildren)
  const Blockquote = createBlockquote(processChildren)
  const Text = createText(processChildren)

  return (
    <div
      className={cn(
        'markdown-display overflow-x-auto overflow-y-hidden',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: Paragraph,
          h1: Heading1,
          h2: Heading2,
          ul: UnorderedList,
          ol: OrderedList,
          li: ListItem,
          code: CodeBlock as never,
          pre: PreBlock,
          strong: Strong,
          em: Em,
          blockquote: Blockquote,
          img: () => null,
          a: () => null,
          table: () => null,
          thead: () => null,
          tbody: () => null,
          tr: () => null,
          td: () => null,
          th: () => null,
          text: Text,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownDisplay
